import React from "react";

import {
  ExternalLink,
  FileText,
  Gauge,
  Link2,
  ListChecks,
  ShieldAlert,
  Sparkles,
  TrendingUp,
  TriangleAlert,
} from "lucide-react";

import {
  ACCENT,
  CitationsModal,
  GroupCard,
  MUTED,
  PointList,
  ScoreChip,
} from "./Chrome";
import { ProfileCard } from "./ExecProfilesModal";
import { useAppSelector } from "../../../Store/Hooks";
import type { XFactorDigestData } from "../../../Types/XFactorBriefing";
import type { XFactorSectionDetail } from "../../../Types/XFactorViewsRendering";
import {
  DEFAULT_HEALTH_SCORE_THRESHOLDS,
  type BusinessHealthThresholds,
} from "../../../Utils/HealthThresholds";
import { SourceList } from "../../XFactorBriefings/components/Primitives";
import { bandOf, buildExecProfiles } from "../lib/derive";

/**
 * xfactor colours the grading words inside a synopsis — "high" green, "medium"
 * amber, "low" red — so the sentence can be skimmed for its verdict.
 */
const GRADE_COLOR: Record<string, string> = {
  high: "#169244",
  strong: "#169244",
  medium: "#ae8404",
  moderate: "#ae8404",
  low: "#b13030",
  weak: "#b13030",
};

export const Graded: React.FC<{ text: string }> = ({ text }) => (
  <>
    {text
      .split(/\b(high|medium|low|strong|moderate|weak)\b/gi)
      .map((part, i) => {
        const tone = GRADE_COLOR[part.toLowerCase()];
        return tone ? (
          <span key={i} style={{ color: tone, fontWeight: 600 }}>
            {part}
          </span>
        ) : (
          <React.Fragment key={i}>{part}</React.Fragment>
        );
      })}
  </>
);

/**
 * Synopsis prose arrives with inline `[c1][c4]` markers. xfactor strips them
 * and hangs a single evidence link off the block instead, so the paragraph
 * reads as prose rather than as a footnoted citation trail.
 */
const REF_MARKER = /\[(c\d+)\]/gi;

const refsIn = (text: string): string[] =>
  Array.from(new Set(Array.from(text.matchAll(REF_MARKER), m => m[1])));

const stripRefs = (text: string): string =>
  text
    .replace(REF_MARKER, "")
    .replace(/\s+([.,;:])/g, "$1")
    .trim();

/** The band words xfactor uses in its "This account shows …" sentence. */
const WORD: Record<string, string> = {
  strong: "high",
  moderate: "medium",
  weak: "low",
  unknown: "unknown",
};

const bandWord = (
  v: number | null | undefined,
  thresholds?: Partial<BusinessHealthThresholds> | null
) => WORD[bandOf(v, thresholds)] || "unknown";

/** A labelled sub-brief inside the Synopsis panel. */
const SubBrief: React.FC<{ label: string; text: string }> = ({
  label,
  text,
}) => (
  <div className="mt-3">
    <div
      className="text-[10px] font-semibold uppercase tracking-wide"
      style={{ color: ACCENT }}
    >
      {label}
    </div>
    <p className="mt-1 leading-relaxed" style={{ fontSize: 13, color: MUTED }}>
      {stripRefs(text)}
    </p>
  </div>
);

/**
 * One section's body, rendered identically wherever it appears.
 *
 * xfactor states this as an invariant — "a section always looks the same
 * wherever it renders" — and it holds: the section detail modal and the Full
 * Briefing show the same AI-Gen SCORES panel, Synopsis panel and tinted group
 * cards. Sharing the component is what keeps that true here.
 *
 * The group order is xfactor's own and is not the payload's: Highlights,
 * Lowlights, Opportunities, Risks, Recommended Actions, Citations.
 */
const SectionBody: React.FC<{
  detail: XFactorSectionDetail;
  /** Anchor ids are only useful where a nav rail points at them. */
  anchored?: boolean;
  /** Needed only for the Leadership section's executive roster. */
  data?: XFactorDigestData | null;
}> = ({ detail, anchored, data }) => {
  const bhThresholds = useAppSelector(
    state =>
      state.vendorAdmin.healthScoreThresholds?.business_health ??
      DEFAULT_HEALTH_SCORE_THRESHOLDS.business_health
  );
  const id = (name: string) => (anchored ? `xf-sec-${name}` : undefined);
  const [showSynopsisRefs, setShowSynopsisRefs] = React.useState(false);

  const profiles = React.useMemo(
    () => (detail.sectionId === "leadership" ? buildExecProfiles(data) : []),
    [detail.sectionId, data]
  );

  // The roster chips scroll to the matching card in Key Content. Refs rather
  // than element ids, so two section bodies on screen at once cannot collide.
  const cardRefs = React.useRef<Array<HTMLDivElement | null>>([]);
  const [flashed, setFlashed] = React.useState<number | null>(null);
  const flashTimer = React.useRef<number | undefined>(undefined);

  React.useEffect(
    () => () => {
      if (flashTimer.current) window.clearTimeout(flashTimer.current);
    },
    []
  );

  const jumpToProfile = (i: number) => {
    cardRefs.current[i]?.scrollIntoView({
      behavior: "smooth",
      block: "center",
    });
    // A short ring, so it is obvious which of eleven cards was landed on.
    setFlashed(i);
    if (flashTimer.current) window.clearTimeout(flashTimer.current);
    flashTimer.current = window.setTimeout(() => setFlashed(null), 1400);
  };

  if (detail.missing) {
    return (
      <p className="text-sm italic text-gray-400">
        This section is not present in the briefing. Upstream omits sections
        silently when the per-org cost cap is hit — that means &ldquo;not
        generated&rdquo;, not &ldquo;failed&rdquo;.
      </p>
    );
  }

  const scoreOf = (label: string) =>
    detail.scores.find(s => s.label === label)?.value ?? null;

  // xfactor closes the synopsis with a plain-language restatement of the four
  // scores, so the numbers above have a sentence a reader can quote.
  const hasBands = detail.scores.some(s => s.value != null);
  const verdict = hasBands
    ? `This account shows ${bandWord(scoreOf("Health"), bhThresholds)} business health, with ` +
      `${bandWord(scoreOf("Fit"), bhThresholds)} relevance. This account presents ` +
      `${bandWord(scoreOf("Risk"), bhThresholds)} risk, with ${bandWord(
        scoreOf("Opportunity"),
        bhThresholds
      )} opportunity.`
    : null;

  const synopsisRefs = detail.synopsis ? refsIn(detail.synopsis) : [];

  /**
   * xfactor prints only Risk and Opportunity on a section's score row, in that
   * order, even though the payload carries `sectionHealthScore` and
   * `sectionRelevanceScore` alongside them. Those two are still read — they
   * drive the header badge and the verdict sentence above — so this trims what
   * is displayed, not what is parsed. The summary section keeps all four,
   * which is what xfactor shows on its own Executive Summary.
   */
  const scoreChips = (
    detail.sectionId === "summary"
      ? detail.scores
      : ["Risk", "Opportunity"].flatMap(label => {
          const score = detail.scores.find(s => s.label === label);
          return score ? [score] : [];
        })
  ).filter(s => s.label !== "Fit");

  return (
    <>
      <GroupCard title="AI-Gen SCORES" Icon={Gauge}>
        <div className="flex flex-wrap items-center gap-2">
          {scoreChips.map(s => (
            <ScoreChip key={s.label} label={s.label} value={s.value} />
          ))}
          <span className="text-[11px] text-gray-500">
            All scores are normalized on 0 – 100 scale — low is bad, high is
            good.
          </span>
        </div>
      </GroupCard>

      {/* The Leadership section opens with its roster as name chips, so the
          cast is visible before any prose. Each chip is the jump link to that
          person's card down in Key Content. */}
      {profiles.length ? (
        /* A fixed three-column grid rather than `flex-wrap`. Wrapping sized
           each chip to its own text, so rows held two or three depending on
           how long the titles were and nothing lined up down the page. Equal
           columns give the roster a spine. Steps down to two and one column so
           the chips do not get too narrow to read on smaller screens. */
        <div className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2 lg:grid-cols-3">
          {profiles.map((p, i) => (
            <button
              key={i}
              type="button"
              onClick={() => jumpToProfile(i)}
              title={`Go to ${p.name}'s profile card`}
              className="cursor-pointer rounded border border-gray-200 bg-white px-2 py-1 text-left leading-snug transition-colors hover:border-[#ad46ff] hover:bg-purple-50"
              style={{ fontSize: 11 }}
            >
              {/* Name and title on their own lines: at a third of the width a
                  long title would otherwise wrap mid-phrase and push the chips
                  to uneven heights. `truncate` keeps each to one line, with the
                  full text already on the button's title attribute. */}
              <span className="block truncate font-semibold text-gray-900">
                {p.name}
              </span>
              {p.title ? (
                <span className="block truncate" style={{ color: MUTED }}>
                  {p.title}
                </span>
              ) : null}
            </button>
          ))}
        </div>
      ) : null}

      {detail.synopsis ||
      detail.highlightsBrief ||
      detail.lowlightsBrief ||
      detail.risksBrief ||
      detail.fitBrief ? (
        <section
          id={id("synopsis")}
          className="mt-3 rounded-lg border px-3 py-2.5"
          style={{ backgroundColor: "#f5f8ff", borderColor: "#dbe6fb" }}
        >
          <h3 className="mb-2 flex items-center gap-2 text-[13px] font-semibold text-gray-900">
            <FileText size={15} style={{ color: "#3b82f6" }} aria-hidden />
            Synopsis
          </h3>

          {detail.synopsis ? (
            <p className="text-sm leading-relaxed text-gray-700">
              {stripRefs(detail.synopsis)}
            </p>
          ) : null}
          {verdict ? (
            <p className="mt-2 text-sm leading-relaxed text-gray-700">
              <Graded text={verdict} />
            </p>
          ) : null}

          {detail.highlightsBrief || detail.lowlightsBrief ? (
            <div>
              <div
                className="mt-3 text-[10px] font-semibold uppercase tracking-wide"
                style={{ color: ACCENT }}
              >
                Highlights &amp; lowlights brief
              </div>
              {detail.highlightsBrief ? (
                <p
                  className="mt-1 leading-relaxed"
                  style={{ fontSize: 13, color: MUTED }}
                >
                  {stripRefs(detail.highlightsBrief)}
                </p>
              ) : null}
              {detail.lowlightsBrief ? (
                <p
                  className="mt-1.5 leading-relaxed"
                  style={{ fontSize: 13, color: MUTED }}
                >
                  {stripRefs(detail.lowlightsBrief)}
                </p>
              ) : null}
            </div>
          ) : null}

          {detail.risksBrief ? (
            <SubBrief
              label="Risks & opportunities brief"
              text={detail.risksBrief}
            />
          ) : null}
          {detail.fitBrief ? (
            <SubBrief label="Fit for organization" text={detail.fitBrief} />
          ) : null}

          {synopsisRefs.length ? (
            <button
              type="button"
              onClick={() => setShowSynopsisRefs(true)}
              title={`${synopsisRefs.length} cited source${
                synopsisRefs.length === 1 ? "" : "s"
              }`}
              aria-label="Show synopsis citations"
              className="mt-2 rounded p-0.5 transition-colors"
              style={{ color: ACCENT }}
            >
              <Link2 className="h-3 w-3" />
            </button>
          ) : null}

          <CitationsModal
            open={showSynopsisRefs}
            onClose={() => setShowSynopsisRefs(false)}
            title={detail.label}
            refs={synopsisRefs}
            citations={detail.citations}
          />
        </section>
      ) : null}

      {detail.highlights.length ? (
        <GroupCard
          id={id("highlights")}
          title="Highlights"
          Icon={Sparkles}
          tone="green"
        >
          <PointList
            points={detail.highlights}
            citations={detail.citations}
            variant="bare"
            hideChip
          />
        </GroupCard>
      ) : null}

      {detail.lowlights.length ? (
        <GroupCard
          id={id("lowlights")}
          title="Lowlights"
          Icon={TriangleAlert}
          tone="red"
        >
          <PointList
            points={detail.lowlights}
            citations={detail.citations}
            variant="bare"
            hideChip
          />
        </GroupCard>
      ) : null}

      {detail.opportunities.length ? (
        <GroupCard
          id={id("opportunities")}
          title="Opportunities"
          Icon={TrendingUp}
          tone="green"
        >
          <PointList
            points={detail.opportunities}
            citations={detail.citations}
            variant="row"
            hideChip
          />
        </GroupCard>
      ) : null}

      {detail.risks.length ? (
        <GroupCard id={id("risks")} title="Risks" Icon={ShieldAlert} tone="red">
          <PointList
            points={detail.risks}
            citations={detail.citations}
            variant="row"
            hideChip
          />
        </GroupCard>
      ) : null}

      {detail.actions.length ? (
        <GroupCard
          id={id("actions")}
          title="Recommended Actions"
          Icon={ListChecks}
          tone="accent"
        >
          <PointList
            points={detail.actions}
            citations={detail.citations}
            variant="row"
            hideChip
          />
        </GroupCard>
      ) : null}

      {profiles.length ? (
        <GroupCard title="Executive Profiles" Icon={FileText}>
          {/* Same card and same grid as the Exec Profile Cards modal, so a
              profile reads the same wherever it appears. */}
          <div className="grid gap-3 md:grid-cols-2">
            {profiles.map((p, i) => (
              <div
                key={i}
                ref={el => {
                  cardRefs.current[i] = el;
                }}
                /* h-full so the wrapper passes the grid's stretched height
                   down to the card rather than absorbing it. */
                className={`h-full scroll-mt-4 rounded-lg transition-shadow duration-500 ${
                  flashed === i ? "ring-2 ring-[#ad46ff]" : ""
                }`}
              >
                <ProfileCard profile={p} />
              </div>
            ))}
          </div>
        </GroupCard>
      ) : null}

      {detail.citations.length ? (
        <GroupCard id={id("citations")} title="Citations" Icon={ExternalLink}>
          <SourceList citations={detail.citations} />
        </GroupCard>
      ) : null}
    </>
  );
};

export default SectionBody;
